<!--
  An input that can contains segments with different styles
  Using weird contenteditable HTML property

  As close as possible from element-ui input
  https://github.com/ElemeFE/element/blob/dev/packages/input/src/input.vue

  To use, it should be extended:
    ex: export default {
          name: "ElWhateverInput",
          extends: "ElColorableInput",
          data () {
            // a field whose value is external to the input,
            // and thus should be kept accross editions and history navigation
            return {
              globalFields: ['field'],
            }
          },
          methods: {
            // turns a string expression into an array of segments
            fromExpression (expression) { ... },
            // turns an array of segments into a string expression
            toExpression (expression) { ... },
            // copy and clean an array of segments
            cleanSegments (segments) { ... },
            /* parse a HTML tag to a segment
                - attrs: a dict of all data-xxx attributes (without the prefix)
                    - type: the segment type
                    - text: the text content
                    - key: the unique key
                    - ...
                - methods: a dict of 3 methods:
                    - get_key(): get a new random key,
                    - push_seg(seg): push a new segment
                    - push_text(text): push a piece of text
                - node: the jquery node */
            parseAny (attrs, methods, node) { ... },
            // same as parseAny, but for text
            parseText (attrs, methods) { ... },
            // same as parseAny, but for a specific type of segment
            // { type: 'myType' } and { type: 'my-type' } will use parseMyType
            parseXXX (attrs, methods, node) { ... },
            /* renders to HTML a segment
                - seg: the segment to render
                - span: the jquery <span> used for rendering
                - attrs: a dict of all data-xxx attributes (without the prefix)
              Returns a jquery node, potentially different from span */
            renderAny(seg, span, attrs) { ... },
            // same as renderAny, but for a specific type of segment
            // { type: 'myType' } and { type: 'my-type' } will use renderMyType
            renderXXX (seg, span, attrs) { ... },
          },
        }

  Props:
  - value: the value as string
  - segments: the array of segments
  - ...: see el-input

  Emits:
  - @input(value): value has changed
  - @change(value): same as @input
  - @update:expression(value): same as @input
  - @update:segments(segments): segments have changed
  - @userupdate: the updates came directly form the user action
  - @selected(selection): a piece of segment has been selected
      ex: null // no selection anymore
      ex: { // a piece of segment selected
            key: "key", // the key of the segment selected,
            start: 2, // the position where the selection starts
            stop: 10, // the position where the selection stops
            text: "text", // the text selected
          }
-->

<template>
  <div :class="[
    'el-input',
    'el-colorable-input',
    inputSize ? 'el-input--' + inputSize : '',
    {
      'is-disabled': inputDisabled,
      'el-input-group': $slots.prepend || $slots.append,
      'el-input-group--append': $slots.append,
      'el-input-group--prepend': $slots.prepend,
      'el-input--prefix': $slots.prefix || prefixIcon,
      'el-input--suffix': $slots.suffix || suffixIcon || clearable,
      'el-input--multiline': multiline,
    }
    ]"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
  >
    <!-- 前置元素 -->
    <div class="el-input-group__prepend" v-if="$slots.prepend">
      <slot name="prepend"></slot>
    </div>
    <div class="el-input__inner el-colorable__placeholder">
      {{copyExpression ? '' : placeholder}}
    </div>
    <!-- eslint-disable vue/use-v-on-exact -->
    <div
      class="el-input__inner el-colorable__editable"
      :contenteditable="inputDisabled||readonly?'false':'true'"
      :tabindex="inputDisabled||readonly?-1:tabindex"
      v-bind="$attrs"
      :disabled="inputDisabled"
      :readonly="readonly"
      ref="input"
      :style="textareaStyle"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown.enter="handleSubmit"
      @mouseup.left="handleSelection"
      @keyup.shift="handleSelection"
      @keyup.ctrl="handleSelection"
      @touchend="handleSelection"
      @keydown.ctrl.90.stop.prevent="handleCtrlZ"
      @keydown.ctrl.89.stop.prevent="handleCtrlY"
      :aria-label="label"
    >
    <!-- eslint-enable -->
      <span ref="once" data-prepos="0" v-once></span>
    </div>
    <!-- 前置内容 -->
    <span class="el-input__prefix" v-if="$slots.prefix || prefixIcon">
      <slot name="prefix"></slot>
      <i class="el-input__icon"
         v-if="prefixIcon"
         :class="prefixIcon">
      </i>
    </span>
    <!-- 后置内容 -->
    <span
      class="el-input__suffix"
      v-if="$slots.suffix || suffixIcon || showClear || validateState && needStatusIcon">
      <span class="el-input__suffix-inner">
        <template v-if="!showClear">
          <slot name="suffix"></slot>
          <i class="el-input__icon"
            v-if="suffixIcon"
            :class="suffixIcon">
          </i>
        </template>
        <i v-else
          class="el-input__icon el-icon-circle-close el-input__clear"
          @click="clear"
        ></i>
      </span>
      <i class="el-input__icon"
        v-if="validateState"
        :class="['el-input__validateIcon', validateIcon]">
      </i>
    </span>
    <!-- 后置元素 -->
    <div class="el-input-group__append" v-if="$slots.append">
      <slot name="append"></slot>
    </div>
  </div>
</template>
<script>
import emitter from 'element-ui/lib/mixins/emitter'
import merge from 'element-ui/lib/utils/merge'
import $ from 'jquery'
import deeEqual from 'fast-deep-equal'
import XRegExp from 'xregexp'

const LETTER = new XRegExp('[\\p{M}\\p{L}\\p{N}]', 'u')
// equivalent to spaces, contenteditable mixes those with normal spaces
const NBSP = String.fromCharCode(160)
const NBSP_REG = new RegExp(NBSP, 'g')
// used for enmpty contenteditable, to avoid back to first line
const EMPTY_CHAR = String.fromCharCode(8203)
const EMPTY_REG = new RegExp(EMPTY_CHAR, 'g')
// this function compares strings while ignoring NBSP
function textEqual (a, b) {
  return (a || '').replace(NBSP_REG, ' ') === (b || '').replace(NBSP_REG, ' ')
}
// removes every useless character
function cleanText (s) {
  return s.replace(EMPTY_REG, '').replace(NBSP_REG, ' ')
}

function parseTextBR (multiline, node, selection) {
  const isSelected = selection && selection.anchorNode === node
  let text
  if (node.nodeType === Node.TEXT_NODE) {
    if (isSelected) {
      return [
        cleanText(node.textContent.substr(0, selection.anchorOffset)),
        cleanText(node.textContent.substr(selection.anchorOffset)),
      ]
    } else {
      return cleanText(node.textContent)
    }
  } else if (node.nodeType !== Node.ELEMENT_NODE) {
    text = ''
  } else if (node.tagName === 'BR') {
    text = multiline ? '\n' : ''
  } else {
    text = multiline && node.tagName === 'DIV' ? '\n' : ''
    const children = node.childNodes
    let length = children.length
    if (isSelected) length = Math.min(length, selection.anchorOffset)
    let i = 0
    for (; i < length; i++) {
      let t = parseTextBR(multiline, children[i], selection)
      if (Array.isArray(t)) {
        text = [text + t[0], t[1]]
        i++
        break
      }
      text += t
    }
    if (isSelected) {
      text = [text, '']
    }
    if (Array.isArray(text)) {
      for (; i < length; i++) {
        text[1] += parseTextBR(multiline, children[i])
      }
      return text
    }
  }
  if (isSelected) return ['', text]
  else return text
}

function renderTextBR ($node, text) {
  const parts = text.replace(/ /g, NBSP).split('\n')
  $node.text(parts[0])
  for (let i = 1; i < parts.length; i++) {
    $node.append('<br>')
    if (parts[i]) {
      $node.append(document.createTextNode(parts[i]))
    }
  }
  return $node
}

export default {
  name: 'ElColorableInput',
  componentName: 'ElInput',

  // copied from el-input
  mixins: [emitter],
  inheritAttrs: false,
  inject: {
    elForm: {
      default: '',
    },
    elFormItem: {
      default: '',
    },
  },

  data () {
    return {
      // the local woking copy of value
      copyExpression: '',
      // the local woking copy of segments
      copySegments: [],
      // the history of edits, to handle ctrl + Z/Y
      history: {
        // a list of all the previous segments state
        list: [],
        // the position the next state should be inserted at,
        // potantially araising preiously cancelled state
        length: 0,
        // an object describing the last edit, in order to merge them
        edit: null,
        // an object containing the global fields by key
        globals: {},
      },
      // the last seen position of the cursor
      position: { absolute: -1 },
      // the position of the cursor just before an input
      prePosition: { absolute: -1 },
      // the position of the cursor just after an input
      postPosition: { absolute: -1 },
      // the edit that has just been made, if any
      edit: null,
      // true just after a user input
      fromInput: false,
      // copied from el-input
      textareaCalcStyle: {},
      hovering: false,
      focused: false,
      // number of read Events since the last parsing
      readEvents: 0,
    }
  },

  props: {
    // the value as a string
    value: String,
    // the value as an array of segments
    segments: Array,
    // copied from el-input
    size: String,
    resize: String,
    form: String,
    disabled: Boolean,
    readonly: Boolean,
    autosize: {
      type: [Boolean, Object],
      default: false,
    },
    validateEvent: {
      type: Boolean,
      default: true,
    },
    suffixIcon: String,
    prefixIcon: String,
    label: String,
    clearable: {
      type: Boolean,
      default: false,
    },
    tabindex: {
      type: [String, Number],
      default: '0',
    },
    placeholder: String,
    multiline: Boolean,
  },

  // copied from el-input
  computed: {
    _elFormItemSize () {
      return (this.elFormItem || {}).elFormItemSize
    },
    validateState () {
      return this.elFormItem ? this.elFormItem.validateState : ''
    },
    needStatusIcon () {
      return this.elForm ? this.elForm.statusIcon : false
    },
    validateIcon () {
      return {
        validating: 'el-icon-loading',
        success: 'el-icon-circle-check',
        error: 'el-icon-circle-close',
      }[this.validateState]
    },
    textareaStyle () {
      if (!this.multiline) return {}
      return merge({}, this.textareaCalcStyle, { resize: this.resize })
    },
    inputSize () {
      return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size
    },
    inputDisabled () {
      return this.disabled || (this.elForm || {}).disabled
    },
    showClear () {
      return this.clearable &&
          !this.inputDisabled &&
          !this.readonly &&
          this.copyExpression &&
          (this.focused || this.hovering)
    },
  },

  watch: {
    // updates copyExpression and copySegments accordingly
    value (val) {
      // from el-input
      this.$nextTick(this.resizeTextarea)

      if (this.copyExpression === val) return
      this.copyExpression = val
      this.copySegments = this.fromExpression(val)
    },
    // sends all the events related to a value update
    copyExpression (val) {
      this.$emit('update:expression', val)
      this.$emit('change', val)
      this.$emit('input', val)
      // copied from el-input
      if (this.validateEvent) {
        this.dispatch('ElFormItem', 'el.form.change', [val])
      }
    },
    // updates copyExpression and copySegments accordingly
    segments (val) {
      this.copySegments = this.cleanSegments(val)
      this.copyExpression = this.toExpression(this.copySegments)
    },
    // rerender the contenteditable div
    copySegments (val, old) {
      let fromInput = this.fromInput
      this.fromInput = false
      // check for in depth equality, to avoid history problems
      if (!deeEqual(val, old)) {
        // rerender the contenteditable div
        if (this.renderHTML()) {
          this.setPosition(this.position.absolute)
        }
        // push the new state
        this.pushHistory(this.copySegments)
        // emit an event
        this.$emit('update:segments', val)
      }
      // emit userupdate if this comes from a use input
      if (fromInput) this.$emit('userupdate')
    },
  },

  methods: {
    // gives focus to the contenteditable and put the cursor at the end
    focus () {
      if (this.focused) return
      const selection = window.getSelection()
      let edit = this.$refs.input
      while (edit.lastChild) edit = edit.lastChild
      let range = document.createRange()
      range.setStart(edit, edit.nodeType === Node.TEXT_NODE ? edit.nodeValue.length : 0)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
      this.handlePosition()
    },
    // inspired from el-input
    blur () {
      this.$refs.input.blur()
    },
    handleBlur (event) {
      this.focused = false
      this.$emit('blur', event)
      if (this.validateEvent) {
        this.dispatch('ElFormItem', 'el.form.blur', [this.value])
      }
    },
    select () {
      this.$refs.input.select()
    },
    handleFocus (event) {
      this.focused = true
      this.$emit('focus', event)
    },
    resizeTextarea () {
      if (this.$isServer) return
      if (!this.multiline) return
      // those values should be found automatically
      const lineHeight = 21, paddingHeight = 12
      if (!this.autosize) {
        this.textareaCalcStyle = {
          minHeight: (lineHeight + paddingHeight) + 'px',
        }
        return
      }
      const minRows = this.autosize.minRows
      const maxRows = this.autosize.maxRows

      this.textareaCalcStyle = {
        minHeight: ((minRows || 1) * lineHeight + paddingHeight) + 'px',
        maxHeight: maxRows && (maxRows * lineHeight + paddingHeight) + 'px',
      }
    },
    calcIconOffset (place) {
      let elList = [].slice.call(this.$el.querySelectorAll(`.el-input__${place}`) || [])
      if (!elList.length) return
      let el = null
      for (let i = 0; i < elList.length; i++) {
        if (elList[i].parentNode === this.$el) {
          el = elList[i]
          break
        }
      }
      if (!el) return
      const pendantMap = {
        suffix: 'append',
        prefix: 'prepend',
      }

      const pendant = pendantMap[place]
      if (this.$slots[pendant]) {
        el.style.transform = `translateX(${place === 'suffix' ? '-' : ''}${this.$el.querySelector(`.el-input-group__${pendant}`).offsetWidth}px)`
      } else {
        el.removeAttribute('style')
      }
    },
    updateIconOffset () {
      this.calcIconOffset('prefix')
      this.calcIconOffset('suffix')
    },
    clear () {
      this.copyExpression = ''
      this.copySegments = []
      this.$emit('clear')
    },
    // called on input events
    handleInput () {
      if (!this.readEvents) {
        this.prePosition = this.position
        setTimeout(() => this.readInput(), 20)
      }
      this.readEvents++
      this.position = this.getPosition()
      this.postPosition = this.position
    },

    readInput () {
      let events = this.readEvents
      this.readEvents = 0
      this.fromInput = true
      // reset selection during text edition
      this.$emit('selected', null)
      // check if it was a mergeable edit, meaning
      // the addition or suppression or one character
      // while not created / deleting a segment
      // console.log('input', this.$refs.once.innerHTML)
      let segs = this.parseHTML()
      let edit = null
      // position was almost the same, at one character difference for each event
      if (this.postPosition.key &&
          this.postPosition.key === this.prePosition.key &&
          segs.length === this.copySegments.length &&
          // new character or characters deleted on the left of the cursor
          (this.postPosition.right === this.prePosition.right &&
          Math.abs(this.postPosition.left - this.prePosition.left) === events ||
          // characters deleted on the right of the cursor
          (this.postPosition.left === this.prePosition.left &&
          this.postPosition.right - this.prePosition.right === -events))) {
        // diff = 1: character written
        // diff = -1: character deleted to the left
        // diff = 0: character deleted to the right
        let diff = this.postPosition.left - this.prePosition.left
        diff = (diff > 0) - (diff < 0)
        // check that this is the only differences
        for (let i = 0; i < segs.length; i++) {
          let a = segs[i], b = this.copySegments[i]
          // check that segments are the same
          if (a.key !== b.key || a.type !== b.type) {
            diff = null
            break
          }
          // for this segment, check that the rest of the cahracters are unchanged
          if (a.key === this.postPosition.key) {
            let mleft = Math.min(this.postPosition.left, this.prePosition.left)
            let mright = Math.min(this.postPosition.right, this.prePosition.right)
            if (mleft && !textEqual(a.text.substr(0, mleft), b.text.substr(0, mleft)) ||
              mright && !textEqual(a.text.substr(-mright), b.text.substr(-mright))) {
              diff = null
              break
            }
          // check that nothing else was changed
          } else if (!textEqual(a.text, b.text)) {
            diff = null
            break
          }
        }
        // create the edit object
        if (diff !== null) {
          edit = {
            absolute: this.postPosition.absolute,
            key: this.postPosition.key,
            right: this.postPosition.right,
            left: this.postPosition.left,
            diff: diff,
          }
        }
      }
      // saves changes
      this.edit = edit
      this.copySegments = segs
      this.copyExpression = this.toExpression(this.copySegments)
    },
    // called when the user potentially finished selecting something
    handleSelection () {
      // check that the selection is valid
      const selection = window.getSelection()
      const anchor = $(selection.anchorNode).closest('.el-colorable__text')
      // not on a text segment
      if (!anchor.length) return this.$emit('selected', null)
      // not on this editor
      if (anchor[0].parentNode !== this.$refs.once) {
        return this.$emit('selected', null)
      }
      // not on one a single segment
      if (!$(selection.focusNode).closest('.el-colorable__text').is(anchor)) {
        return this.$emit('selected', null)
      }
      // nothing selected
      if (selection.anchorNode === selection.focusNode &&
        selection.anchorOffset === selection.focusOffset) {
        return this.$emit('selected', null)
      }
      // look for the positions of the selection
      let i, anchorPos = 0, focusPos = 0
      const children = anchor[0].childNodes
      for (i = 0; i < children.length; i++) {
        if (children[i] === selection.anchorNode) {
          anchorPos += selection.anchorOffset
          break
        } else {
          anchorPos += parseTextBR(this.multiline, children[i]).length
        }
      }
      if (children[i] !== selection.anchorNode) {
        return this.$emit('selected', null)
      }
      for (i = 0; i < children.length; i++) {
        if (children[i] === selection.focusNode) {
          focusPos += selection.focusOffset
          break
        } else {
          focusPos += parseTextBR(this.multiline, children[i]).length
        }
      }
      if (children[i] !== selection.focusNode) {
        return this.$emit('selected', null)
      }
      // move the position of the selection to correct
      // forgotten characters or spaces
      let start = Math.min(anchorPos, focusPos)
      let stop = Math.max(anchorPos, focusPos)
      const text = parseTextBR(this.multiline, anchor[0])
      while (start < stop && !text[start].match(LETTER)) start++
      while (start > 0 && text[start - 1].match(LETTER)) start--
      while (stop > start && !text[stop - 1].match(LETTER)) stop--
      while (stop < text.length && text[stop].match(LETTER)) stop++
      if (start >= stop) return this.$emit('selected', null)
      // emit the selection
      let out = {
        key: anchor.attr('data-key'),
        start: start,
        stop: stop,
        text: text,
      }
      this.$emit('selected', out)
    },
    // submit to the parent form on enter press
    handleSubmit (event) {
      if (this.multiline) return
      event.preventDefault()
      $(this.$refs.once).closest('form').find('[type="submit"]').click()
    },
    // default parseText method, does the minmum
    parseText (seg, methods) {
      methods.push_seg(seg)
    },
    // pases the contentedtable div
    parseHTML () {
      const segs = [], el = this.$refs.once,
        ce = this.$refs.input
      let hasel = false, prepos = 0, sibi
      // the list of text segments, to merge as one
      let text_accu = [], text_key = null
      // look for children other than el, and add them as text
      for (sibi = 0; sibi < ce.childNodes.length; sibi++) {
        let child = ce.childNodes[sibi]
        if (child === el) {
          hasel = true
          $(el).attr('data-prepos', prepos + '')
          break
        }
        let text = parseTextBR(this.multiline, child)
        if (text) {
          prepos += text.length
          text_accu.push(text)
        }
      }
      // merges all text parts, and pushes thmen to segments
      const text_push = () => {
        if (text_accu.length) {
          segs.push({
            type: 'text',
            text: text_accu.join(''),
            key: text_key || Math.random() + '',
          })
        }
        text_accu = []
        text_key = null
      }
      // local methods passed to pushText
      const text_methods = {
        // pushes a segment
        push_seg (seg) {
          text_push() // finally push the text met until then
          segs.push(seg)
        },
        // pushes a text for final merging
        push_text (text, key) {
          if (!text) return
          text_accu.push(text)
          if (!text_key && key) text_key = key
        },
        // generates a "unique" key
        get_key () {
          return Math.random() + ''
        },
      }
      // merges all text parts, and sends it to parseText for the final merge
      const text_join = () => {
        if (text_accu.length) {
          let text = text_accu.join(''), key = text_key
          // reset accu for parseText call
          text_accu = []
          text_key = null
          // call parseText
          this.parseText({
            type: 'text',
            text: text,
            key: key || Math.random() + '',
          }, text_methods)
          // push whatever remains
          text_push()
        }
        // reset accu for the next segment
        text_accu = []
        text_key = null
      }
      // el has been removed, probably while emptying the field
      if (!hasel) {
        $(el).attr('data-prepos', prepos + '')
        text_join()
        return segs
      }
      // local methods passed to pushXXX
      const seg_methods = {
        ...text_methods,
        // pushes a segment
        push_seg (seg) {
          text_join()
          segs.push(seg) // call parseText on the text me until then
        },
      }
      // read segment tags in el
      for (let i = 0; i < el.childNodes.length; i++) {
        let child = el.childNodes[i]
        let text = parseTextBR(this.multiline, child)
        let type, key, attrs = {}
        if (!text) {
          continue
        }
        // this is an HTML tag
        if (child.nodeType === Node.ELEMENT_NODE) {
          let $child = $(child)
          key = $child.filter('span.el-colorable__segment').attr('data-key')
          // this is a real segment tag
          if (key) {
            // check if it's a copy of the next tag
            for (; i < el.childNodes.length - 1; i++) {
              let next = el.childNodes[i + 1]
              if (next.nodeType === Node.TEXT_NODE) break
              let $next = $(next)
              if (key !== $next.filter('span.el-colorable__segment').attr('data-key')) break
              // next tag has the same key, merge them
              text += parseTextBR(this.multiline, next)
              // the first tag is more likely to be the copy, keep the second tag
              child = next
              $child = $next
            }
            type = $child.attr('data-type')
            for (let i = 0; i < child.attributes.length; i++) {
              let attr = child.attributes[i]
              if (attr.name.substr(0, 'data-'.length) === 'data-') {
                attrs[attr.name.substr('data-'.length)] = attr.value || true
              }
            }
          }
        }
        attrs.text = text
        // parseXXX function to be defined by
        // extending component for specific behavior
        let fun = type && ('parse-' + type).replace(/[-_]([a-z]?)/g, (_, c) => c.toUpperCase())
        fun = fun && this[fun] || this.parseAny
        // ignore empty
        if (type === 'empty') continue
        // a text segment, adds it to the accu
        else if (!type || type === 'text') {
          text_accu.push(text)
          if (!text_key && key) text_key = key
        // the function has been defined by extending component, call it
        } else if (type !== 'text' && fun) {
          fun(attrs, seg_methods, $(child))
        // no specific behvior, just add it
        } else if (text) {
          text_join()
          segs.push(attrs)
        }
      }
      // look for children other than el, and add them as text
      for (sibi++; sibi < ce.childNodes.length; sibi++) {
        let child = ce.childNodes[sibi]
        let text = parseTextBR(this.multiline, child)
        if (text) {
          text_accu.push(text)
        }
      }
      // add the remaining text if any
      text_join()
      return segs
    },
    // renders the contenteditable, tries to touch HTML as few as possible
    renderHTML (full) {
      const el = $(this.$refs.once),
        ce = $(this.$refs.input)
      // empty content, add an empty char for cursor position
      if (!this.copySegments.length) {
        ce.empty().append(el.empty()
          .append($('<span>').addClass('el-colorable__empty')
            .attr({ 'data-type': 'empty' }).text(EMPTY_CHAR)))
        return true
      }
      // list all elements
      let elements = [], keys = {}
      this.copySegments.forEach((seg) => {
        keys[seg.key] = true
        let attrs = {}
        // gather data-xxx attributes
        for (let name in seg) {
          if (name !== 'text') attrs['data-' + name] = seg[name]
        }
        // renderXXX function to be defined by
        // extending component for specific behavior
        let fun = ('render-' + seg.type).replace(/[-_]([a-z]?)/g, (_, c) => c.toUpperCase())
        fun = this[fun] || this.renderAny
        // the function has been defined by extending component, call it
        if (fun) {
          let span = $('<span>')
            .addClass('el-colorable__segment el-colorable__' + seg.type)
            .attr({ 'data-type': seg.type, 'data-key': seg.key })
            .text(seg.text)
          span = fun(seg, span, attrs) || span
          elements.push(renderTextBR(span, span.text()))
        // generic tag
        } else {
          elements.push(renderTextBR($('<span>')
            .addClass('el-colorable__segment el-colorable__' + seg.type)
            .attr(attrs), seg.text))
        }
      })
      // too weird, just insert all the elements
      if (full || !el.parent(ce).length || ce[0].childNodes.length !== 1) {
        ce.empty().append(el.empty().append(elements))
        return true
      }
      // try to touch the HTML as few as possible
      let posChanged = false // if any operation broke the position
      let eli = 0, chi = 0
      while (true) {
        // no more elements, clear every remaining child of el
        if (eli >= elements.length) {
          while (chi < el[0].childNodes.length) {
            // console.log('remove', el[0].childNodes[chi])
            el[0].childNodes[chi].remove() // chi is now next node
            posChanged = true
          }
          break
        // no more child in el, insert all the remaining elements
        } else if (chi >= el[0].childNodes.length) {
          for (; eli < elements.length; eli++) {
            // console.log('append', elements[eli])
            el.append(elements[eli])
            posChanged = true
          }
          break
        }
        let node = el[0].childNodes[chi]
        let element = elements[eli][0]
        // not the node we expect, remove it
        if (node.nodeType !== Node.ELEMENT_NODE || node.tagName !== 'SPAN') {
          // console.log('remove', node)
          node.remove() // chi is now next node
          posChanged = true
          continue
        }
        let key = node.attributes['data-key']
        // if successive tags have the same key, only keep the last one
        if (key) {
          while (chi < el[0].childNodes.length - 1) {
            let next = el[0].childNodes[chi + 1]
            if (next.nodeType !== Node.ELEMENT_NODE || next.tagName !== 'SPAN') break
            let nextKey = node.attributes['data-key']
            if (!nextKey || key.value !== nextKey.value) break
            // remove the previous node
            // console.log('remove', node)
            node.remove()
            posChanged = true
            node = next
          }
        }
        // console.log('key', key, (node.attributes['data-type'] || {}).value)
        if (!key && (node.attributes['data-type'] || {}).value === 'empty') {
          key = element.attributes['data-key']
        }
        // no key, or not in the segments anymore, remove
        if (!key || !keys[key.value]) {
          // console.log('remove', node)
          node.remove() // chi is now next node
          posChanged = true
          continue
        }
        delete keys[key.value] // in case of two children have the same key
        // the key of the child hasn't been encountered yet, insert element
        if (element.attributes['data-key'].value !== key.value) {
          // console.log('insertBefore', element, node)
          el[0].insertBefore(element, node)
          posChanged = true
          eli++ // next element
          chi++ // same node
          continue
        }
        // the node has a weird content, replace it with element
        let weird = false
        for (let i = 0; i < node.childNodes.length; i++) {
          let child = node.childNodes[i]
          if (child.nodeType !== Node.TEXT_NODE &&
            !(child.nodeType === Node.ELEMENT_NODE && child.tagName === 'BR')) {
            weird = true
            break
          }
        }
        if (weird) {
          // console.log('replaceWith', element, node)
          node.replaceWith(element)
          posChanged = true
          eli++ // next element
          chi++ // next node
          continue
        }
        // check for too many attributes
        for (let i = 0; i < node.attributes.length; i++) {
          let attr = node.attributes[i].name
          if (!(attr in element.attributes)) {
            node.removeAttribute(attr)
          }
        }
        // check for attributes value
        for (let i = 0; i < element.attributes.length; i++) {
          let attr = element.attributes[i]
          if (attr.value !== (node.attributes[attr.name] || {}).value) {
            node.setAttribute(attr.name, attr.value)
          }
        }
        // check for text content
        let content = node.innerHTML.replace(/ /g, '&nbsp;').replace(EMPTY_REG, '')
        if (element.innerHTML !== content) {
          // console.log('content', node.innerHTML, element.innerHTML)
          node.replaceChildren(...element.childNodes)
          posChanged = true
        }
        eli++ // next element
        chi++ // next node
      }
      // console.log('posChanged', posChanged)
      return posChanged
    },
    // retrieves the current position of the cursor
    getPosition () {
      const once = this.$refs.once,
        selection = window.getSelection()
      const anchor = $(selection.anchorNode)
      // clicked on a suggestion, don't change anything
      if (anchor.closest('.el-autocomplete-suggestion').length) {
        return {
          ...this.position,
        }
      }
      // the selection is not inside once
      if (!anchor.closest(once).length) {
        if (anchor.closest(this.$refs.input).length) {
          return { absolute: parseInt(once.attr('data-prepos')) || 0 }
        } else {
          return { absolute: -1 }
        }
      }

      let position = 0, text, left, right = 0, segment
      // count the absolute position until the cursor
      const multiline = this.multiline
      if (selection.anchorNode === once) {
        right = 0
        for (let i = 0; i < selection.anchorOffset; i++) {
          let child = once.childNodes[i]
          text = parseTextBR(multiline, child)
          left = text.length
          position += left
          segment = $(child)
        }
      } else {
        for (let i = 0; i < once.childNodes.length; i++) {
          let child = once.childNodes[i]
          if (anchor.closest(child).length) {
            text = parseTextBR(multiline, child, selection)
            left = text[0].length
            right = text[1].length
            text = text[0] + text[1]
            position += left
            break
          } else position += parseTextBR(multiline, child).length
        }
        segment = anchor.closest('span.el-colorable__segment')
        segment = segment.length ? segment : null
      }
      // adds some useful infos about the cursor
      let rect = selection.rangeCount ? selection.getRangeAt(0) : null
      rect = rect && rect.getClientRects()[0]
      return {
        absolute: position,
        key: segment && segment.attr('data-key'),
        type: segment && segment.attr('data-type'),
        left: left,
        right: right,
        text: text,
        rect: rect,
        el: segment,
      }
    },
    // puts back the cursor at the desired position
    setPosition (position) {
      let pos = position
      if (pos < 0) return
      const selection = window.getSelection()
      let el = this.$refs.once
      let segment = null, text = '', left = 0, right = 0
      // check elements until the asolute position is found
      const multiline = this.multiline
      let found = false
      while (!found && pos > 0 && el.nodeType === Node.ELEMENT_NODE) {
        for (let i = 0; ; i++) {
          if (i >= el.childNodes.length) {
            pos = i
            found = true
            break
          }
          const child = el.childNodes[i]
          const t = parseTextBR(multiline, child)
          const length = t.length
          if (length < pos) {
            pos -= length
            continue
          }
          if ($(child).is('span.el-colorable__segment')) {
            segment = $(child)
            text = t
            left = pos
            right = length - pos
          }
          if (length === pos) {
            pos = i + 1
            found = true
            break
          }
          el = child
          break
        }
      }
      let range = document.createRange()
      range.setStart(el, pos)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
      let rect = range.getClientRects()[0]
      this.position = {
        absolute: position,
        key: segment && segment.attr('data-key'),
        type: segment && segment.attr('data-type'),
        left: left,
        right: right,
        text: text,
        rect: rect,
        el: segment,
      }
    },
    // creates a deep clone of segments
    cloneSegments (segs) {
      return segs.map((seg) => {
        let copy = { ...seg }
        for (let field of this.globalFields || []) {
          delete copy[field]
        }
        return copy
      })
    },
    // adds a new segments state in history, with the cursor position
    pushHistory (segs) {
      let copy = this.cloneSegments(segs)
      // remove globalFields and seve them in history.globals
      segs.forEach((seg) => {
        let gattrs = {}
        for (let field of this.globalFields || []) {
          if (field in seg) gattrs[field] = seg[field]
        }
        this.history.globals[seg.key] = gattrs
      })
      // check that the new state is different
      if (deeEqual(copy, this.history.list[this.history.length - 1])) return
      // check if this edit can be merged with the previous one
      let edit = this.history.edit
      copy.position = this.position
      // both edits can be merge
      if (edit && this.edit &&
          edit.key === this.edit.key &&
          edit.diff === this.edit.diff &&
          (edit.diff && edit.right === this.edit.right ||
          !edit.diff && edit.left === this.edit.left)) {
        // just replace the last hostory entry
        this.history.list[this.history.length - 1] = copy
        // update absolute position to detect position change
        this.history.edit.absolute = this.edit.absolute
      } else {
        // add a new history entry
        this.history.list[this.history.length] = copy
        this.history.length++
        this.history.edit = this.edit
      }
      // the cursor has moved before the input was read, close the edit
      if (this.history.edit && this.history.edit.absolute !== this.position.absolute) {
        this.history.edit = null
      }
      // no possible CTRL+Y now, so cut the history array
      this.history.list.length = this.history.length
      this.edit = null
    },
    // restores a state from the history
    popHistory (redo) {
      // check which history item should be loaded
      if (redo) {
        if (this.history.length >= this.history.list.length) return
        this.history.length++
      } else {
        if (this.history.length < 2) return
        this.history.length--
      }
      // cannot merge edits after that
      this.history.edit = null
      // rebuild segments with global fields
      let hist = this.history.list[this.history.length - 1]
      let copy = this.cloneSegments(hist)
      copy.forEach((seg) => {
        let gattrs = this.history.globals[seg.key]
        if (gattrs) {
          for (let field in gattrs) seg[field] = gattrs[field]
        }
      })
      // restore the state
      this.position = hist.position
      this.copySegments = copy
      this.copyExpression = this.toExpression(copy)
    },
    // called by ctrl+Z, also manages ctrl+shift+Z
    handleCtrlZ (e) {
      this.popHistory(e.shiftKey)
    },
    // called by ctrl+Y
    handleCtrlY () {
      this.popHistory(true)
    },
    // called on document selectionchange event
    handlePosition () {
      let pos = this.getPosition()
      // already updated by handleInput, nothing to do
      if (pos.absolute === this.position.absolute &&
        pos.key === this.position.key &&
        pos.left === this.position.left &&
        pos.right === this.position.right &&
        pos.type === this.position.type &&
        pos.text === this.position.text) return
      this.position = pos
      // updates position in history
      let hist = this.history.list[this.history.length - 1]
      if (hist.position.absolute !== pos.absolute) {
        // updates current position in history (for the last redo)
        hist.position = pos
        // stop edit merging
        this.history.edit = null
      }
    },

    // to be inheritted, cleans a segments Array
    cleanSegments (segs) { return [...segs] },
    // to be inheritted, converts segments to expression
    toExpression (segs) { return segs.map(seg => seg.text).join('') },
    // to be inheritted, converts expression to segments
    fromExpression (expr) {
      return [{
        type: 'text',
        text: expr,
        key: Math.random() + '',
      }]
    },
  },

  created () {
    // init copyExpression and copySegments using the props
    if (this.value) {
      this.copyExpression = this.value
      this.copySegments = this.fromExpression(this.value)
    } else if (this.segments) {
      this.copySegments = this.cleanSegments(this.segments)
      this.copyExpression = this.toExpression(this.copySegments)
    }
    // push the first state of the history
    this.pushHistory(this.copySegments)
    // listen selectionchange
    document.addEventListener('selectionchange', this.handlePosition)
    // copied from el-input
    this.$on('inputSelect', this.select)
  },

  mounted () {
    // render the contenteditable for the first time
    this.renderHTML(true)
    // copied from el-input
    this.resizeTextarea()
    this.updateIconOffset()
  },

  // copied from el-input
  updated () {
    this.$nextTick(this.updateIconOffset)
  },

  beforeDestroy () {
    // stop listening selectionchange
    document.removeEventListener('selectionchange', this.handlePosition)
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  /* avoid weird CSS bugs with height */
  .el-input.el-colorable-input {
    height: $--input-height;
    /* to enable "scroll on select" */
    &:not(.el-input--multiline):not(.is-disabled) {
      display: inline-table; // to enable "scroll on select"
    }
    /* forces the text on the left, leaves the content transparent for the placeholder */
    .el-input__inner.el-colorable__editable {
      line-height: $--input-height - 2px;
      text-align: start;
      background-color: transparent !important;
    }
    &.el-input--multiline .el-input__inner.el-colorable__editable {
      line-height: inherit;
      padding: 10px 15px;
      height: auto;
    }
    /* the placeholder fills the background of the input */
    .el-input__inner.el-colorable__placeholder {
      position: absolute;
      text-align: start;
      border-color: transparent !important;
      color: $--color-text-placeholder;
      z-index: -100;
      height: 100%;
    }
    /* ignores new lines */
    .el-input__inner.el-colorable__editable,
    .el-input__inner.el-colorable__editable * {
      white-space: nowrap;
      overflow: hidden;
    }
    &.el-input--multiline .el-input__inner.el-colorable__editable {
      overflow: auto;
    }
    &:not(.el-input--multiline) .el-input__inner.el-colorable__editable br {
      display:none;
    }
  }
</style>
